import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { birimListesi, personelListesi } from '../api/organizasyon';
import { geciktirmeKullan } from '../kancalar/geciktirme';
import { Dugme, HataKutusu } from '../bilesenler/temel';

/**
 * PERSONEL LİSTESİ.
 *
 * Birim süzgeci adres çubuğunda taşınır (`?birimId=`): birim ağacındaki
 * "24 kişi" düğmesi buraya bağlanıyor ve bağlantının paylaşılabilir olması
 * gerekiyor. Bileşen durumunda tutulsaydı bağlantı süzgeci taşımazdı.
 */
const SAYFA_BOYUTU = 25;

export function PersonelListesi() {
  const [adresSorgusu, adresSorgusuYaz] = useSearchParams();
  const [arama, aramaYaz] = useState('');
  const [sayfa, sayfaYaz] = useState(1);

  const birimId = adresSorgusu.get('birimId') ?? undefined;

  // Her tuş vuruşunda sorgu koşmasın; yazma durunca istek gider.
  const gecikmisArama = geciktirmeKullan(arama);

  const birimler = useQuery({ queryKey: ['birim', 'liste'], queryFn: () => birimListesi() });

  /*
   * ÖNBELLEK ANAHTARI İKİ SEVİYELİ: ['personel', 'liste', ...].
   *
   * Kart ekranı ['personel', 'kart', id] kullanıyor. Tek seviyeli olsaydı
   * `invalidateQueries({ queryKey: ['personel'] })` — personel güncelleme
   * ekranı geldiğinde kaçınılmaz — listeyi ve kartı birlikte geçersizleştirir,
   * ön ek eşleşmesi yüzünden ilgisiz sorgular da tetiklenirdi.
   */
  const liste = useQuery({
    queryKey: ['personel', 'liste', { birimId, arama: gecikmisArama, sayfa }],
    queryFn: () =>
      personelListesi({
        birimId,
        arama: gecikmisArama || undefined,
        sayfa,
        sayfaBoyutu: SAYFA_BOYUTU,
      }),
    // Sayfa değişince tablo boşalıp zıplamasın; eski veri yenisi gelene dek durur.
    placeholderData: keepPreviousData,
  });

  const toplam = liste.data?.toplam ?? 0;
  const sonSayfa = Math.max(1, Math.ceil(toplam / SAYFA_BOYUTU));

  const birimSec = (yeni: string) => {
    // Süzgeç değişince sayfa 1'e döner; aksi halde 4. sayfada boş liste görünür.
    sayfaYaz(1);
    if (yeni) adresSorgusuYaz({ birimId: yeni });
    else adresSorgusuYaz({});
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Personel</h1>
        <p className="mt-1 text-sm text-slate-600">
          Yalnız yetkiniz kapsamındaki birimler listelenir.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          value={arama}
          onChange={(olay) => {
            sayfaYaz(1);
            aramaYaz(olay.target.value);
          }}
          placeholder="Ad, soyad veya personel numarası"
          className="min-w-56 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
        />

        <select
          value={birimId ?? ''}
          onChange={(olay) => birimSec(olay.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500"
        >
          <option value="">Tüm birimler</option>
          {/*
            SÜZGEÇ MÜDÜRLÜK VE ŞEFLİKLE SINIRLI (K3–K4).
            `GET /organizasyon/birim` sayfalama yapmıyor; altı kademenin
            tamamı listelenseydi 3.000 personelde bu açılır kutu yüzlerce
            satır olurdu. Ekip düzeyinde süzme birim ağacı ekranından yapılır.
          */}
          {birimler.data
            ?.filter((b) => b.kademeSeviye >= 3 && b.kademeSeviye <= 4)
            .map((b) => (
              <option key={b.birimId} value={b.birimId}>
                {b.ad}
              </option>
            ))}
        </select>
      </div>

      <HataKutusu hata={liste.error ?? birimler.error} />

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2 font-medium">Personel no</th>
              <th className="px-4 py-2 font-medium">Ad Soyad</th>
              <th className="px-4 py-2 font-medium">Birim</th>
              <th className="px-4 py-2 font-medium">Pozisyon</th>
              <th className="px-4 py-2 font-medium">İstihdam</th>
              <th className="px-4 py-2 font-medium">Hesap</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {liste.data?.kayitlar.map((p) => (
              <tr key={p.personelId} className="hover:bg-slate-50">
                <td className="px-4 py-2 font-mono text-xs text-slate-600">{p.personelNo}</td>
                <td className="px-4 py-2">
                  <Link to={`/personel/${p.personelId}`} className="text-kurum-700 hover:underline">
                    {p.adSoyad}
                  </Link>
                  {p.cikisTarihi && (
                    <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
                      ayrıldı
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-slate-700">{p.birimAd ?? '—'}</td>
                <td className="px-4 py-2 text-slate-700">{p.pozisyonKod ?? '—'}</td>
                <td className="px-4 py-2 text-slate-700">{p.istihdamTipiKod}</td>
                <td className="px-4 py-2">
                  {p.kullanicisiVarMi ? (
                    <span className="text-xs text-slate-500">var</span>
                  ) : (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                      yok
                    </span>
                  )}
                </td>
              </tr>
            ))}

            {liste.data?.kayitlar.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  Kayıt bulunamadı.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-4 text-sm text-slate-600">
        <span>
          {toplam} kayıt · sayfa {sayfa}/{sonSayfa}
        </span>
        <div className="flex gap-2">
          <Dugme
            tur="ikincil"
            tikla={() => sayfaYaz((s) => Math.max(1, s - 1))}
            pasif={sayfa <= 1}
            cocuk="Önceki"
          />
          <Dugme
            tur="ikincil"
            tikla={() => sayfaYaz((s) => Math.min(sonSayfa, s + 1))}
            pasif={sayfa >= sonSayfa}
            cocuk="Sonraki"
          />
        </div>
      </div>
    </div>
  );
}
